import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiPlay, FiVolume2, FiVolumeX, FiHeart, FiMessageCircle, FiBookmark, FiShare2 } from 'react-icons/fi';
import { MdVerified } from 'react-icons/md';
import { CATEGORIES } from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';
import LoginPrompt from '../LoginPrompt/LoginPrompt';
import { favoritesAPI, reactionsAPI } from '../../services/api';
import { resolveMediaUrl } from '../../utils/media';
import './VideoCard.css';

const VideoCard = ({ post }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [userReaction, setUserReaction] = useState(null);
  const [reactionCounts, setReactionCounts] = useState({
    like: 0,
    love: 0,
    smart: 0,
    wow: 0,
    think: 0
  });
  const [isSaved, setIsSaved] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const videoRef = useRef(null);

  const category = CATEGORIES.find(cat => cat.id === post.category);
  const posterUrl = post?.image ? resolveMediaUrl(post.image) : undefined;

  const slugify = (text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const getPostSlug = () => {
    if (post?.slug) return post.slug;
    const base = slugify(post?.title) || 'video';
    return post?.id ? `${base}-${post.id}` : base;
  };

  const isLoved = userReaction === 'love';
  const loveCount = useMemo(() => {
    const fromReactions = post?.reactions?.love;
    const fromCounts = reactionCounts?.love;
    const fromLegacy = post?.likes;
    if (Number.isFinite(fromCounts)) return fromCounts;
    if (Number.isFinite(fromReactions)) return fromReactions;
    return Number.isFinite(fromLegacy) ? fromLegacy : 0;
  }, [post, reactionCounts]);

  useEffect(() => {
    setReactionCounts({
      like: post?.reactions?.like ?? 0,
      love: post?.reactions?.love ?? post?.love_count ?? 0,
      smart: post?.reactions?.smart ?? 0,
      wow: post?.reactions?.wow ?? 0,
      think: post?.reactions?.think ?? 0
    });
  }, [post]);

  useEffect(() => {
    let isMounted = true;
    if (!isAuthenticated || !post?.id) return;

    (async () => {
      try {
        const [reactionRes, favRes] = await Promise.all([
          reactionsAPI.getUserReaction(post.id),
          favoritesAPI.check(post.id)
        ]);
        if (!isMounted) return;
        setUserReaction(reactionRes.data?.userReaction || reactionRes.data?.reactionType || null);
        setIsSaved(!!favRes.data?.isFavorited);
      } catch (e) {
        // ignore
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, post?.id]);

  const openVideo = () => {
    const slug = getPostSlug();
    navigate(`/video/${slug}`, { state: { backgroundLocation: location } });
  };

  const handleCardClick = (e) => {
    if (e.target.closest('a') || e.target.closest('button') || e.target.closest('.video-controls')) {
      return;
    }
    // Open via URL (modal overlay handled by App routing)
    openVideo();
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 24) {
      return `Hace ${diffInHours}h`;
    } else if (diffInHours < 168) {
      return `Hace ${Math.floor(diffInHours / 24)}d`;
    } else {
      return date.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <>
      <article className="video-card" onClick={handleCardClick} style={{ cursor: 'pointer' }}>
        <div className="video-player-container">
          {post.video_url ? (
            <video
              ref={videoRef}
              src={resolveMediaUrl(post.video_url)}
              className="video-player"
              muted={isMuted}
              loop
              playsInline
              poster={posterUrl}
              preload="metadata"
              onLoadedData={() => {
                if (!posterUrl && videoRef.current) {
                  try {
                    // Nudge to paint a frame as thumbnail
                    videoRef.current.currentTime = 0.1;
                  } catch (e) {
                    // ignore
                  }
                }
              }}
            />
          ) : (
            <img
              src={resolveMediaUrl(post.image)}
              alt={post.title}
              className="video-player"
            />
          )}
          
          <div className="video-overlay video-controls">
            <button className="play-button" onClick={(e) => {
              e.stopPropagation();
              togglePlay();
            }}>
              <FiPlay />
            </button>
            
            <button className="mute-button" onClick={(e) => {
              e.stopPropagation();
              toggleMute();
            }}>
              {isMuted ? <FiVolumeX /> : <FiVolume2 />}
            </button>

            <div className="video-duration">
              {post.duration || '5:24'}
            </div>
          </div>

          {category && (
            <span className="video-category" style={{ background: category.color }}>
              {category.icon} {category.name}
            </span>
          )}
        </div>

        <div className="video-content">
          <Link to={`/video/${getPostSlug()}`} state={{ backgroundLocation: location }} className="video-title" onClick={(e) => e.stopPropagation()}>
            {post.title}
          </Link>

          <p className="video-description">{post.excerpt}</p>

          <div className="video-meta">
            <Link to={`/${post.author?.username || post.author?.id}`} className="author-info" onClick={(e) => e.stopPropagation()}>
              <img 
                src={resolveMediaUrl(post.author.avatar)} 
                alt={post.author.name}
                className="author-avatar"
              />
              <div>
                <span className="author-name">
                  {post.author.name}
                  {post.author?.verification_level && post.author.verification_level !== 'none' && (
                    <MdVerified
                      style={{
                        marginLeft: 6,
                        verticalAlign: 'middle',
                        color: post.author.badge_color || 'currentColor'
                      }}
                      title={post.author.badge_label || 'Cuenta verificada'}
                    />
                  )}
                </span>
                <span className="post-date">{formatDate(post.createdAt)}</span>
              </div>
            </Link>

            <div className="video-actions">
              <button 
                className={`action-button ${isLoved ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isAuthenticated) {
                    setShowLoginPrompt(true);
                    return;
                  }
                  (async () => {
                    try {
                      const res = await reactionsAPI.toggle(post.id, 'love');
                      setUserReaction(res.data?.userReaction || null);
                      setReactionCounts(res.data?.reactions || {});
                    } catch (err) {
                      // ignore
                    }
                  })();
                }}
              >
                <FiHeart />
                <span>{loveCount}</span>
              </button>

              <button className="action-button" onClick={(e) => {
                e.stopPropagation();
                if (!isAuthenticated) {
                  setShowLoginPrompt(true);
                  return;
                }
                openVideo();
              }}>
                <FiMessageCircle />
                <span>{post.comments}</span>
              </button>

              <button 
                className={`action-button ${isSaved ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isAuthenticated) {
                    setShowLoginPrompt(true);
                    return;
                  }
                  (async () => {
                    try {
                      if (isSaved) {
                        const res = await favoritesAPI.remove(post.id);
                        setIsSaved(!!res.data?.isFavorited);
                      } else {
                        const res = await favoritesAPI.add(post.id);
                        setIsSaved(!!res.data?.isFavorited);
                      }
                    } catch (err) {
                      // ignore
                    }
                  })();
                }}
              >
                <FiBookmark />
              </button>

              <button className="action-button" onClick={(e) => {
                e.stopPropagation();
                const url = `${window.location.origin}/video/${getPostSlug()}`;
                if (navigator.share) {
                  navigator.share({ title: post.title, url });
                } else {
                  navigator.clipboard.writeText(url);
                }
              }}>
                <FiShare2 />
              </button>
            </div>
          </div>
        </div>
      </article>
      
      {showLoginPrompt && (
        <LoginPrompt 
          message="Inicia sesión para interactuar con este video"
          onClose={() => setShowLoginPrompt(false)}
        />
      )}
    </>
  );
};

export default VideoCard;
